import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useTrainingResults } from "@/hooks/putting/useTrainingResults";
import PuttingTrainingHeader from "@/components/putting/training/PuttingTrainingHeader";
import PuttingHistoryTabs from "@/components/putting/training/PuttingHistoryTabs";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import MultiTestResultsChart from "@/components/putting/training/MultiTestResultsChart";

const PuttingTraining = () => {
  const { t } = useTranslation('common');
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  
  // Utilisation du hook spécifique pour les entraînements
  const { 
    trainingResults, 
    isLoading, 
    chartDataByTest,
    testTypes,
    level1Results, 
    level2Results, 
    level3Results 
  } = useTrainingResults();

  // Filtrer STRICTEMENT les résultats liés uniquement au putting
  const puttingResults = trainingResults.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    
    // Exclure les tests "Standard" (réservés aux tests de Pelz)
    if (!result.test_level) {
      return false;
    }
    
    // Liste explicite des IDs de tests de putting connus
    const puttingTestIds = [
      'putts-plat', 
      'putts-montee', 
      'putts-descente', 
      'putts-pente', 
      'putts-pente-droite'
    ];
    
    // Vérification stricte - doit être un test de putting explicite
    return (
      // Vérifier si c'est un ID connu de test de putting
      puttingTestIds.includes(testId) ||
      // Ou si l'ID commence par "putts-" (test de putting spécifique)
      testId.startsWith('putts-') ||
      // Ou si c'est clairement un test de putting par le nom et pas un autre type
      (testName.includes('putt') && 
       !testName.includes('chip') && 
       !testName.includes('wedg') && 
       !testName.includes('bunker') && 
       !testName.includes('iron') && 
       !testName.includes('driv') &&
       !testName.includes('bois'))
    );
  });
  
  // Appliquer le même filtre strict aux résultats par niveau
  const puttingLevel1Results = level1Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    
    // Exclure les tests "Standard" (réservés aux tests de Pelz)
    if (!result.test_level) {
      return false;
    }
    
    const puttingTestIds = [
      'putts-plat', 
      'putts-montee', 
      'putts-descente', 
      'putts-pente', 
      'putts-pente-droite'
    ];
    
    return (
      puttingTestIds.includes(testId) ||
      testId.startsWith('putts-') ||
      (testName.includes('putt') && 
       !testName.includes('chip') && 
       !testName.includes('wedg') && 
       !testName.includes('bunker') && 
       !testName.includes('iron') && 
       !testName.includes('driv') &&
       !testName.includes('bois'))
    );
  });
  
  const puttingLevel2Results = level2Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    
    // Exclure les tests "Standard" (réservés aux tests de Pelz)
    if (!result.test_level) {
      return false;
    }
    
    const puttingTestIds = [
      'putts-plat', 
      'putts-montee', 
      'putts-descente', 
      'putts-pente', 
      'putts-pente-droite'
    ];
    
    return (
      puttingTestIds.includes(testId) ||
      testId.startsWith('putts-') ||
      (testName.includes('putt') && 
       !testName.includes('chip') && 
       !testName.includes('wedg') && 
       !testName.includes('bunker') && 
       !testName.includes('iron') && 
       !testName.includes('driv') &&
       !testName.includes('bois'))
    );
  });
  
  const puttingLevel3Results = level3Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    
    // Exclure les tests "Standard" (réservés aux tests de Pelz)
    if (!result.test_level) {
      return false;
    }
    
    const puttingTestIds = [
      'putts-plat', 
      'putts-montee', 
      'putts-descente', 
      'putts-pente', 
      'putts-pente-droite'
    ];
    
    return (
      puttingTestIds.includes(testId) ||
      testId.startsWith('putts-') ||
      (testName.includes('putt') && 
       !testName.includes('chip') && 
       !testName.includes('wedg') && 
       !testName.includes('bunker') && 
       !testName.includes('iron') && 
       !testName.includes('driv') &&
       !testName.includes('bois'))
    );
  });

  // Recalculer chartDataByTest et testTypes uniquement à partir des résultats filtrés de putting
  // Cela exclura automatiquement "Entraînement putting" (tests standard sans test_level)
  const puttingChartDataByTest = React.useMemo(() => {
    if (puttingResults.length === 0) return {};
    
    const chartData = puttingResults
      .slice()
      .sort((a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime())
      .map(result => ({
        date: new Date(result.test_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        points: result.points,
        level: result.test_level || 'N/A',
        testName: result.test_name || 'Standard',
        testId: result.test_id || ''
      }));
    
    const groupedData: Record<string, any[]> = {};
    chartData.forEach(dataPoint => {
      const key = dataPoint.testName || dataPoint.testId || 'Standard';
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(dataPoint);
    });
    
    return groupedData;
  }, [puttingResults]);
  
  const puttingTestTypes = React.useMemo(() => {
    if (puttingResults.length === 0) return [];
    
    const testNames = new Set<string>();
    puttingResults.forEach(result => {
      const testName = result.test_name || result.test_id || 'Standard';
      testNames.add(testName);
    });
    
    return Array.from(testNames);
  }, [puttingResults]);
  
  console.log("Types de tests de putting:", puttingTestTypes);
  console.log("Résultats de putting filtrés:", puttingResults.length);
  
  // Gérer le changement de niveau dans les tabs
  const handleLevelChange = (level: string | null) => {
    setSelectedLevel(level);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="absolute top-20 left-6">
        <Button 
          variant="ghost" 
          className="text-golf-600 hover:text-golf-700 flex items-center gap-2" 
          asChild
        >
          <Link to="/training">
            <ChevronLeft className="h-5 w-5" />
            {t('puttingTraining.backToTraining')}
          </Link>
        </Button>
      </div>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <PuttingTrainingHeader />
        
        <div className="my-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Results Chart - Now in the left wider column */}
          <div className="lg:col-span-2">
            <MultiTestResultsChart 
              chartDataByTest={puttingChartDataByTest}
              testTypes={puttingTestTypes}
              selectedLevel={selectedLevel}
              trainingResults={puttingResults}
            />
          </div>
          
          {/* Results History - Now in the right narrower column */}
          <div className="lg:col-span-1">
            <PuttingHistoryTabs 
              puttingResults={puttingResults}
              level1Results={puttingLevel1Results}
              level2Results={puttingLevel2Results}
              level3Results={puttingLevel3Results}
              onLevelChange={handleLevelChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PuttingTraining;
